import {forwardRef, useEffect, useRef, Ref, MutableRefObject} from 'react';
import moment, {Moment} from 'moment';
import {Box, Card, CardActionArea, Stack, Typography, useTheme} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {generateMomentDateRange} from '../../../utils/functions/generate-moment-date-range';
import './date-carousel.scss';

const NUM_DAYS_BEFORE_AFTER = 4;

interface DateCardProps {
  date: Moment;
  selectedDate: Moment;
  onClick: () => void;
}

const DateCard = forwardRef(
  ({date, selectedDate, onClick}: DateCardProps, ref: Ref<HTMLDivElement> | null) => {
    const today = moment(new Date());
    const isToday = date.isSame(today, 'day');
    const isSelectedDate = date.isSame(selectedDate, 'day');
    const {t} = useTranslation();

    let dayLabel;
    if (isToday) {
      dayLabel = t(translations.daily_care_log.date_carousel.today);
    } else if (date.isSame(today.clone().subtract(1, 'day'), 'day')) {
      dayLabel = t(translations.daily_care_log.date_carousel.yesterday);
    } else if (date.isSame(today.clone().add(1, 'day'), 'day')) {
      dayLabel = t(translations.daily_care_log.date_carousel.tomorrow);
    } else {
      dayLabel = date.format('ddd');
    }

    return (
      <Card ref={ref} className={`date-card ${isSelectedDate && 'selected-date'}`}>
        <CardActionArea onClick={onClick}>
          <Typography className="weekday">{dayLabel}</Typography>
          <Typography className="day">{date.format('DD')}</Typography>
          <Typography className="month">{date.format('MMM')}</Typography>
        </CardActionArea>
      </Card>
    );
  }
);

interface DateCarouselProps {
  selectedDate: Moment;
  onDateChanged: (newDate: Moment) => void;
}

const DateCarousel = ({selectedDate, onDateChanged}: DateCarouselProps) => {
  const start = moment(selectedDate).subtract(NUM_DAYS_BEFORE_AFTER, 'days');
  const end = moment(selectedDate).add(NUM_DAYS_BEFORE_AFTER, 'days');
  const dateRange = generateMomentDateRange(start, end);

  const theme = useTheme();
  const selectedCardRef = useRef() as MutableRefObject<HTMLDivElement>;

  useEffect(() => {
    selectedCardRef.current.scrollIntoView({block: 'nearest', inline: 'center'});
  }, [selectedDate]);

  return (
    <Box
      className="date-carousel-container"
      sx={{backgroundColor: theme.palette.background.default}}
    >
      <Stack className="date-carousel" direction="row" spacing={{xs: 2, md: 3}}>
        {dateRange.map((date) => (
          <DateCard
            ref={date.isSame(selectedDate, 'day') ? selectedCardRef : null}
            date={date}
            selectedDate={moment(selectedDate)}
            key={date.toISOString()}
            onClick={() => onDateChanged(date)}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default DateCarousel;
